// Slice-internal Konva renderer for persons (TR5).
//
// The person slice produces a `Konva.Group` containing a transparent hit-area
// `Konva.Rect`, the chosen avatar as a `Konva.Image` and a name `Konva.Text`
// label. The avatar motifs are multi-colour illustrations (see
// `person-icons.config.ts`), so they are drawn as an image rather than a single
// tinted `Konva.Path`.

import Konva from 'konva';
import type { SliceRenderContext } from '../../core/object-registry.js';
import type { PersonObject } from './person.interface.js';
import { BOARD_TEXT_LINE_HEIGHT } from '../../constants/whiteboard-config.constants.js';
import { PERSON_ICONS, DEFAULT_PERSON_ICON } from './person-icons.config.js';
import { PERSON_FONT_SIZE } from './person.types.js';
import { konvaFontStyle } from '../../objects/konva-decoration.helper.js';

export const PERSON_ICON_RATIO = 0.68;
export const PERSON_NAME_TEXT = 'person-name';
/** Vertical breathing room between the icon's baseline and the name label. */
export const PERSON_ICON_TEXT_GAP = 8;
// Board-Hausschrift D-DIN mit System-Fallback (siehe STICKY_FONT_FAMILY).
const PERSON_FONT_FAMILY = '"D-DIN", system-ui, -apple-system, sans-serif';

/** Re-exported so existing importers keep their `person.renderer.js` path. */
export { PERSON_FONT_SIZE };

const PERSON_HIT_NAME = 'person-hit';
const PERSON_ICON_NAME = 'person-icon';

/**
 * Position + size box for the person's chosen avatar image. The avatars have
 * (slightly) DIFFERING viewBoxes, so the box height is the target icon height
 * and the width follows the avatar's own aspect ratio; it is then centred
 * horizontally. A person without an explicit `icon` falls back to
 * `DEFAULT_PERSON_ICON`.
 */
export function iconBox(obj: PersonObject): { x: number; width: number; height: number } {
  const def = PERSON_ICONS[obj.icon ?? DEFAULT_PERSON_ICON];
  const height = obj.height * PERSON_ICON_RATIO;
  const aspect = def.vbHeight > 0 ? def.vbWidth / def.vbHeight : 1;
  const width = height * aspect;
  const x = (obj.width - width) / 2;
  return { x, width, height };
}

/**
 * Ring width as a fraction of the kreis radius, at the DEFAULT border-width
 * level (4). Other levels scale linearly (level/4), so level 8 doubles it and
 * level 1 quarters it. Keeping it relative to the radius makes a given level
 * look identical on every avatar regardless of its viewBox size.
 */
const KREIS_RING_RATIO = 0.12;
/** Border-width level used when a person has a ring colour but no explicit width. */
const DEFAULT_RING_LEVEL = 4;

/** The kreis recolour/shape inputs pulled from a PersonObject. */
interface KreisTint {
  fill?: string;
  stroke?: string;
  /** Shared border-width level: 0 = no ring, 1/2/4/8 thin→thick. */
  strokeWidth?: number;
  strokeStyle?: 'solid' | 'dashed' | 'dotted';
  /** Fill opacity 0..1 for the background circle (unset → fully opaque). */
  fillOpacity?: number;
}

/** SVG `stroke-dasharray` for a ring of the given width, or `''` for solid. */
function ringDashArray(style: KreisTint['strokeStyle'], w: number): string {
  if (style === 'dashed') return `${(w * 2).toFixed(3)} ${(w * 1.4).toFixed(3)}`;
  // Dotted = zero-length dashes with round caps → round dots, spaced by ~1.8w.
  if (style === 'dotted') return `0 ${(w * 1.8).toFixed(3)}`;
  return '';
}

/**
 * Recolour / reshape ONLY the avatar's `id="kreis"` background circle from the
 * person's kreis properties; the multi-colour figure drawn on top is untouched.
 * Avatars without an `id="kreis"` element are returned unchanged, so this is safe
 * for motifs that don't (yet) carry it.
 *
 * fill / fillOpacity are applied to the kreis circle itself. The ring, however,
 * is drawn as a SEPARATE, slightly smaller circle: the kreis fills the whole
 * viewBox (its radius reaches every edge), so a stroke placed ON it would be
 * centred on that edge and have its outer half clipped away — the bug Anke saw.
 * The inset ring's outer edge stays just inside the viewBox, so the full ring is
 * visible while the fill still reaches the edge without a gap.
 */
export function tintKreis(svg: string, t: KreisTint): string {
  const level = t.strokeWidth ?? DEFAULT_RING_LEVEL;
  const drawRing = t.stroke !== undefined && level > 0;
  const touchesFill = t.fill !== undefined || t.fillOpacity !== undefined;
  if (!drawRing && !touchesFill) return svg;

  return svg.replace(/<circle\s+id="kreis"[^>]*\/>/, (tag) => {
    let circle = tag;
    if (t.fill !== undefined) {
      circle = /\bfill="[^"]*"/.test(circle)
        ? circle.replace(/\bfill="[^"]*"/, `fill="${t.fill}"`)
        : circle.replace(/\s*\/>$/, ` fill="${t.fill}"/>`);
    }
    if (t.fillOpacity !== undefined) {
      circle = /\bfill-opacity="[^"]*"/.test(circle)
        ? circle.replace(/\bfill-opacity="[^"]*"/, `fill-opacity="${t.fillOpacity}"`)
        : circle.replace(/\s*\/>$/, ` fill-opacity="${t.fillOpacity}"/>`);
    }
    if (!drawRing) return circle;

    const cx = /\bcx="([0-9.]+)"/.exec(tag)?.[1] ?? '0';
    const cy = /\bcy="([0-9.]+)"/.exec(tag)?.[1] ?? '0';
    const r = Number(/\br="([0-9.]+)"/.exec(tag)?.[1]);
    if (!Number.isFinite(r) || r <= 0) return circle;

    const width = r * KREIS_RING_RATIO * (level / DEFAULT_RING_LEVEL);
    // Inset the ring so its outer edge (ringR + width/2) lands a touch inside the
    // viewBox edge (r) — leaves ~10% of the width as clear margin, no clipping.
    const ringR = r - width * 0.6;
    const dash = ringDashArray(t.strokeStyle, width);
    const dashAttr = dash ? ` stroke-dasharray="${dash}"` : '';
    const capAttr = t.strokeStyle === 'dotted' ? ' stroke-linecap="round"' : '';
    const ring =
      `<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="none"` +
      ` stroke="${t.stroke}" stroke-width="${width}"${dashAttr}${capAttr}/>`;
    return circle + ring;
  });
}

/** Pull the kreis tint inputs out of a PersonObject. */
function kreisTint(obj: PersonObject): KreisTint {
  return {
    fill: obj.circleFill,
    stroke: obj.circleStroke,
    strokeWidth: obj.circleStrokeWidth,
    strokeStyle: obj.circleStrokeStyle,
    fillOpacity: obj.backgroundOpacity,
  };
}

/**
 * Signature capturing everything baked into the rendered bitmap: the motif key
 * plus every `kreis` property (colours, ring width + style, fill opacity).
 * `updateNode` reloads only when it changes. The drop shadow is NOT here — it is
 * a Konva node attribute, applied without rebuilding the image.
 */
function avatarSignature(obj: PersonObject): string {
  const key = obj.icon ?? DEFAULT_PERSON_ICON;
  return [
    key,
    obj.circleFill ?? '',
    obj.circleStroke ?? '',
    obj.circleStrokeWidth ?? '',
    obj.circleStrokeStyle ?? '',
    obj.backgroundOpacity ?? '',
  ].join('|');
}

/**
 * Das fertig getoente Motiv-SVG als Zeichenkette.
 *
 * Auch vom SVG-Export genutzt (core/export): Der bettet das Motiv als ECHTE
 * Vektorpfade ein, statt die Pixel-Fassung zu nehmen — dieselbe Toenung, nur
 * ohne Umweg ueber die Data-URL.
 */
export function avatarSvgString(obj: PersonObject): string {
  const key = obj.icon ?? DEFAULT_PERSON_ICON;
  return tintKreis(PERSON_ICONS[key].svg, kreisTint(obj));
}

/** Data-URL per (motif + kreis properties), built once and reused. */
const avatarUrlCache = new Map<string, string>();
function avatarDataUrl(obj: PersonObject): string {
  const sig = avatarSignature(obj);
  let url = avatarUrlCache.get(sig);
  if (url === undefined) {
    url = `data:image/svg+xml,${encodeURIComponent(avatarSvgString(obj))}`;
    avatarUrlCache.set(sig, url);
  }
  return url;
}

/**
 * Soft drop shadow behind the avatar image, toggled by `boxShadow`. Konva draws
 * an image's shadow from its alpha, so a round avatar casts a round shadow.
 * Applied as node attrs (no bitmap rebuild). Values are world units, matching
 * the shape slices' shadow look.
 */
function applyAvatarShadow(node: Konva.Image, obj: PersonObject): void {
  if (obj.boxShadow === 'on') {
    node.setAttrs({
      shadowColor: 'rgba(0,0,0,0.35)',
      shadowBlur: 12,
      shadowOffsetX: 0,
      shadowOffsetY: 4,
      shadowEnabled: true,
    });
  } else {
    node.shadowEnabled(false);
  }
}

/**
 * Load the (possibly tinted) avatar SVG into the given image node. The chosen
 * motif is recorded as `avatarKey` and the full render signature as `avatarSig`
 * so `updateNode` can skip a reload when nothing bitmap-relevant changed. Image
 * decoding is asynchronous, so once it finishes we repaint the node's layer.
 * Guarded for non-DOM environments (unit tests) where `Image` is absent — the
 * node then simply carries no bitmap but keeps its box + attrs.
 */
function loadAvatar(node: Konva.Image, obj: PersonObject): void {
  node.setAttr('avatarKey', obj.icon ?? DEFAULT_PERSON_ICON);
  node.setAttr('avatarSig', avatarSignature(obj));
  if (typeof Image === 'undefined') return;
  const el = new Image();
  el.onload = (): void => {
    node.image(el);
    node.getLayer()?.batchDraw();
  };
  el.src = avatarDataUrl(obj);
  // Some engines decode a data-URL SVG synchronously — use it right away.
  if (el.complete && el.naturalWidth > 0) node.image(el);
}

/**
 * Typography attrs for the name label. Every field falls back to what the
 * label looked like before it became configurable, so a person persisted
 * without these properties renders unchanged. `textColor` deliberately
 * defaults to `color`: until the user picks a text colour, the fill swatches
 * keep tinting icon and name together.
 */
function nameAttrs(obj: PersonObject): Konva.TextConfig {
  return {
    fill: obj.textColor ?? obj.color,
    fontSize: obj.fontSize ?? PERSON_FONT_SIZE,
    fontStyle: konvaFontStyle(obj.fontWeight, obj.fontStyle === 'italic'),
    textDecoration: obj.textDecoration ?? '',
  };
}

/**
 * Centre the icon + name block vertically inside the bounding box, keeping
 * PERSON_ICON_TEXT_GAP between them.
 *
 * Must run AFTER the label's text/size attrs are set: the label's height is
 * left on Konva's `auto` so it reflects the wrapped line count, and that is
 * what makes the block height — and therefore the centring — correct for a
 * one-line "Alice" as well as a name that wraps onto three lines.
 *
 * Clamped at 0 so a block taller than the box still starts at the top edge
 * rather than being pushed above it.
 */
function applyBlockLayout(
  icon: Konva.Node | undefined,
  nameText: Konva.Text | undefined,
  obj: PersonObject,
): void {
  const iconH = obj.height * PERSON_ICON_RATIO;
  const textH = nameText ? nameText.height() : 0;
  const contentH = iconH + PERSON_ICON_TEXT_GAP + textH;
  const offsetY = Math.max(0, (obj.height - contentH) / 2);
  icon?.y(offsetY);
  nameText?.y(offsetY + iconH + PERSON_ICON_TEXT_GAP);
}

/**
 * Anke, 2026-07-22 — den AUTO-breiten Namen mittig auf die Person setzen und
 * dafür sorgen, dass überstehender Text die Skalier-Box NICHT vergrößert.
 *
 * Warum auto-breit: Konva schneidet einen Text mit FESTER Breite ab, sobald eine
 * Zeile breiter wird (führende Zeichen bleiben, der Rest wird gekappt — genau das
 * „linksbündig + abgeschnitten", das Anke sah). Nur ohne feste Breite zeichnet
 * Konva den vollen Text. `align: 'center'` greift dann aber nicht mehr (die Box
 * IST der Text), also wird über `x` zentriert: der Text sitzt mittig auf der
 * Avatar-Box und ragt bei Überlänge symmetrisch über beide Seiten hinaus.
 *
 * Der Haken: Ein überstehender Text vergrößert normalerweise den Client-Rect der
 * Gruppe — und der Transformer misst daran seine Skalier-Box (Person-Resize-
 * Fehler von 2026-07-16). Darum meldet der Knoten seine Trefferbox über ein
 * überschriebenes `getSelfRect` weiterhin nur mit AVATAR-Breite; gezeichnet wird
 * trotzdem der volle Text. So bleibt die Skalier-Box exakt die Objekt-Box.
 */
function centerName(nameText: Konva.Text, obj: PersonObject): void {
  const box = iconBox(obj);
  const textW = nameText.width(); // auto-Breite = natürliche Textbreite
  const x = box.x + (box.width - textW) / 2; // mittig auf die Avatar-Box (ggf. negativ)
  nameText.x(x);
  // Gemeldete Box (Client-Rect) auf die Avatar-Breite klemmen — in LOKALEN
  // Text-Koordinaten (Ursprung = `x`), damit sie nach dem x-Versatz wieder auf
  // `box.x…box.x+box.width` in Gruppen-Koordinaten liegt.
  nameText.getSelfRect = () => ({
    x: box.x - nameText.x(),
    y: 0,
    width: box.width,
    height: nameText.height(),
  });
}

/** Small, constant inset between the docking box and the visible person. */
export const PERSON_SELECT_PADDING = 3;

/**
 * World-space box of the VISIBLE person (avatar + name) with a small inset — the
 * person's anchorable box (see `SlicePlugin.anchorBounds`). Lines dock here
 * instead of at the larger, mostly empty object box, so connector handles and
 * docked endpoints hug the person.
 *
 * Anmerkung (Anke, 2026-07-16): Der AUSWAHLRAHMEN umarmt den Inhalt nicht mehr —
 * er sitzt jetzt an der vollen Objekt-Box, damit die Person korrekt skaliert
 * (siehe updateNode). Das Andocken von Linien bleibt aber eng an der Figur,
 * genau dafuer ist diese Funktion da.
 *
 * Rein: approximiert die Label-Hoehe mit einer Zeile (`fontSize`), was fuer den
 * haeufigen einzeiligen Namen genau passt und bei Umbruch nahe bleibt.
 */
export function personAnchorBounds(obj: PersonObject): { x: number; y: number; width: number; height: number } {
  const box = iconBox(obj);
  const nameH = obj.fontSize ?? PERSON_FONT_SIZE;
  const contentH = box.height + PERSON_ICON_TEXT_GAP + nameH;
  const iconY = Math.max(0, (obj.height - contentH) / 2);
  const p = PERSON_SELECT_PADDING;
  return {
    x: obj.x + box.x - p,
    y: obj.y + iconY - p,
    width: box.width + 2 * p,
    height: contentH + 2 * p,
  };
}

/** Build a fresh Konva.Group rendering the person. */
export function createNode(obj: PersonObject, _ctx: SliceRenderContext): Konva.Group {
  const group = new Konva.Group({
    id: obj.id, x: obj.x, y: obj.y,
    width: obj.width, height: obj.height,
    rotation: obj.rotation, name: 'person-obj',
  });

  const box = iconBox(obj);

  // Transparent hit area covering the full bounding box — the icon and
  // nameText both have listening:false so without this rect the group
  // would receive no pointer events and could not be selected.
  const hitArea = new Konva.Rect({
    name: PERSON_HIT_NAME,
    x: 0, y: 0,
    width: obj.width,
    height: obj.height,
    fill: 'rgba(0,0,0,0)',
  });

  // The avatar is a multi-colour illustration → drawn as an image, scaled into
  // its box. No `fill`: the motif keeps its own colours (the fill swatches no
  // longer tint it).
  const icon = new Konva.Image({
    name: PERSON_ICON_NAME,
    x: box.x,
    width: box.width,
    height: box.height,
    image: undefined,
    listening: false,
  });
  loadAvatar(icon, obj);
  applyAvatarShadow(icon, obj);

  // Anke, 2026-07-22 — KEINE feste `width` (auto): sonst schneidet Konva einen
  // zu langen Namen ab. Der Name bricht auch nicht automatisch um; nur ein
  // MANUELLER Umbruch (`\n`, per Shift+Enter im Editor) erzeugt eine neue Zeile
  // (`wrap: 'none'` — Konva teilt Text ohnehin an `\n`). Die Höhe hängt damit
  // nur an der Zahl manueller Zeilen, nicht an der Textlänge. Zentriert +
  // Client-Rect-Klemmung erledigt `centerName`. Kein `height`: Konva meldet die
  // eigene Höhe, die `applyBlockLayout` zum Zentrieren des Blocks braucht.
  const nameText = new Konva.Text({
    name: PERSON_NAME_TEXT,
    text: obj.text,
    fontFamily: PERSON_FONT_FAMILY,
    // Board-wide text line height (Anke, 2026-10-03; Konva's default is 1).
    // The name editor overlay mirrors it (`o2d-whiteboard.element.ts`).
    lineHeight: BOARD_TEXT_LINE_HEIGHT,
    verticalAlign: 'top',
    wrap: 'none',
    listening: false,
    ...nameAttrs(obj),
  });

  centerName(nameText, obj);
  applyBlockLayout(icon, nameText, obj);
  // Die Trefferflaeche deckt die VOLLE Objekt-Box (in createNode oben schon so
  // angelegt). Siehe updateNode fuer die Begruendung.

  group.add(hitArea);
  group.add(icon);
  group.add(nameText);
  return group;
}

/** Apply changed properties from an updated PersonObject onto its node. */
export function updateNode(node: Konva.Node, obj: PersonObject, _ctx: SliceRenderContext): void {
  if (!(node instanceof Konva.Group)) return;
  const box = iconBox(obj);
  const icon = node.findOne<Konva.Image>(`.${PERSON_ICON_NAME}`);
  if (icon) {
    icon.setAttrs({ x: box.x, width: box.width, height: box.height });
    // Reload the bitmap only when the motif OR a kreis property actually changed.
    if (icon.getAttr('avatarSig') !== avatarSignature(obj)) loadAvatar(icon, obj);
    // Shadow is a cheap node attr — always reconcile, no bitmap rebuild.
    applyAvatarShadow(icon, obj);
  }
  const nameText = node.findOne<Konva.Text>(`.${PERSON_NAME_TEXT}`);
  if (nameText) {
    nameText.setAttrs({
      // Weder `width` noch `height` fest: beide auf Konvas `auto`, damit ein
      // langer Name nicht abgeschnitten wird und die gemessene Block-/Textbreite
      // frisch bleibt (Anke, 2026-07-22).
      width: undefined,
      height: undefined,
      text: obj.text,
      ...nameAttrs(obj),
    });
    // Neu zentrieren + Client-Rect wieder auf Avatar-Breite klemmen (die Breite
    // hat sich mit dem neuen Text geändert).
    centerName(nameText, obj);
  }
  // After the label's attrs — its height feeds the centring.
  applyBlockLayout(icon, nameText, obj);
  // Trefferflaeche = VOLLE Objekt-Box (Anke, 2026-07-16).
  //
  // Frueher umarmte sie den Inhalt eng (fitHitArea), damit der Auswahlrahmen
  // knapp an der Figur sitzt. Das war der Grund fuer den Skalier-Fehler: Der
  // Konva-Transformer misst seine Skalier-Box aus dem Inhalt der Gruppe (der
  // umarmenden Trefferflaeche), nicht aus obj.width/height. Weil die
  // Inhalts-Box durch feste Abstaende (Innenabstand, Zeilenabstand, Schrift)
  // KEINE lineare Kopie der Objekt-Box ist, driftete die feste Ecke beim
  // Skalieren. Mit voller Box stimmen Skalier-Box und gespeicherte Box wieder
  // ueberein — die Person skaliert exakt wie Haftnotiz/Rechteck. Der Rahmen
  // sitzt dafuer ein paar Pixel weiter (mit Anke abgestimmt). Das Andocken von
  // Linien bleibt eng an der Figur: personAnchorBounds rechnet weiter mit dem
  // Inhaltskasten.
  const hitArea = node.findOne<Konva.Rect>(`.${PERSON_HIT_NAME}`);
  hitArea?.setAttrs({ x: 0, y: 0, width: obj.width, height: obj.height });
}
